import { useEffect, useState, useRef, useCallback } from 'react';
import DailyIframe from '@daily-co/daily-js';
import { useWebSocket } from '../../../WebSocketContext';
import useSentryLogger from '../../../hooks/useSentryLogger';
import { message } from 'antd';
import useIsScreenShared from '../../../hooks/useIsScreenShared';
import logger from './../../../utils/logger.js';


// Helper function to detect mobile devices
function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// Helper function to find front camera from device list
async function getFrontCameraId() {
  try {
    // Request permission first to ensure we can access devices
    await navigator.mediaDevices.getUserMedia({ video: true });

    // Get all video devices
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevices = devices.filter(device => device.kind === 'videoinput');

    logger.debug('Available video devices:', videoDevices);

    // Try to find a device with 'front' in the label (common on mobile)
    let frontCamera = videoDevices.find(device =>
      device.label.toLowerCase().includes('front') ||
      device.label.toLowerCase().includes('user') ||
      device.label.toLowerCase().includes('selfie'));

    // If no explicit front camera found and we have multiple cameras, use the first one
    // (on most mobile devices, the first camera is the front camera)
    if (!frontCamera && videoDevices.length > 0) {
      if (isMobileDevice()) {
        // On mobile, the front camera is typically the first one
        frontCamera = videoDevices[0];
      } else {
        // On desktop/laptop, the integrated webcam is usually what we want
        frontCamera = videoDevices.find(device =>
          device.label.toLowerCase().includes('integrated') ||
          device.label.toLowerCase().includes('built-in'));

        // If still no match, use the first camera
        if (!frontCamera && videoDevices.length > 0) {
          frontCamera = videoDevices[0];
        }
      }
    }

    return frontCamera ? frontCamera.deviceId : null;
  } catch (error) {
    logger.error('Error getting front camera:', error);
    return null;
  }
}



// Global instance ref to maintain singleton pattern
let dailyFrameRef = { current: null };
let activeRoomUrl = null;
let isJoining = false;


// URL parser helper
function getUrlAndParams(fullUrl) {
  try {
    const urlObj = new URL(fullUrl);
    const baseUrl = urlObj.origin + urlObj.pathname;
    const params = Object.fromEntries(urlObj.searchParams.entries());

    return { baseUrl, params };
  } catch (error) {
    logger.error("Invalid URL:", error);
    return { baseUrl: fullUrl, params: {} };
  }
}

/**
 * Custom hook for using Daily.js iframe with hidden UI
 */
const useDaily = (options = {}) => {
  // Extract options
  const {
    containerId = 'hidden-daily-container',
    onJoined,
    onLeft,
    onError,
    onParticipantJoined,
    onParticipantLeft,
    onParticipantUpdated
  } = options;

  // Component state
  const [callState, setCallState] = useState('idle'); // idle, joining, joined, leaving, left, error
  const [participants, setParticipants] = useState({});
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);

  // Get WebSocket context for event reporting
  const { ws,wsMessageEvent } = useWebSocket();

  // Get Sentry logger
  const {
    logCallStateChange,
    logParticipantEvent,
    logVideoCallEvent,
    logVideoCallError,
    logDeviceEvent,
    logDeviceError,
    logScreenShareEvent,
    logScreenShareError,
    logMessage
  } = useSentryLogger();

  // Function to send screen record permitted event
  const screenRecordPermitted = useCallback(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
     setTimeout(() => {
      ws.send(JSON.stringify({
        type: "screen_record_permitted",
      }));
     }, 10000);
      logger.debug('Screen record permitted event sent');
    } else {
      logger.warn('WebSocket not connected for screen record permitted event. State:', ws?.readyState);
    }
  }, [ws]);

  // Common function to send video call events to WebSocket
  const sendVideoCallEvent = useCallback((eventType, eventData = {}) => {
    if (ws && ws.readyState === WebSocket.OPEN) {


      try {

        const updatedData = {
          ...eventData,
          name: eventType,
          timestamp: new Date().toISOString()
        };

        // logger.debug('Sending video call event:', eventType, updatedData);


        ws.send(JSON.stringify({
          type: 'video_call_event',
          event_type: eventType,
          data: updatedData
        }));
        return true;
      } catch (error) {
        logger.error(`Error sending ${eventType} event to WebSocket:`, error);
        return false;
      }
    }
    return false;
  }, [ws]);

  // Store component's own reference to the Daily frame
  const frameRef = useRef(null);

  // Store callbacks in a ref to prevent unnecessary re-renders
  const callbacksRef = useRef({
    onJoined,
    onLeft,
    onError,
    onParticipantJoined,
    onParticipantLeft,
    onParticipantUpdated
  });

  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = {
      onJoined,
      onLeft,
      onError,
      onParticipantJoined,
      onParticipantLeft,
      onParticipantUpdated
    };
  }, [onJoined, onLeft, onError, onParticipantJoined, onParticipantLeft, onParticipantUpdated]);

  // Make sure we have a container for the iframe
  const ensureContainer = useCallback(() => {
    let container = document.getElementById(containerId);

    if (!container) {
      // Create hidden container
      container = document.createElement('div');
      container.id = containerId;
      container.style.width = '0px'; // Slightly larger to help with debugging
      container.style.height = '0px';
      container.style.position = 'fixed';
      container.style.right = '0';
      container.style.bottom = '0';
      container.style.opacity = '0'; // More visible for debugging
      container.style.pointerEvents = 'none'; // Prevents interaction
      container.style.zIndex = '-1'; // Behind other elements
      container.style.border = '1px solid red'; // For debugging

      // Add to document
      document.body.appendChild(container);
      logger.debug('Created Daily container:', container);
    }

    return container;
  }, [containerId]);

  // Helper function to ensure front camera is used
  const ensureFrontCamera = useCallback(async () => {
    if (!frameRef.current || frameRef.current._destroyed || callState !== 'joined') {
      logger.debug('Cannot set camera: not in a call');
      return;
    }

    try {
      logger.debug('Attempting to set front camera...');

      // First try to find a front camera device ID
      const frontCameraId = await getFrontCameraId();

      if (frontCameraId) {
        // Use setCamera with the front camera device ID
        logger.debug('Setting specific front camera:', frontCameraId);
        await frameRef.current.setCamera(frontCameraId);
      } else {
        // If we couldn't identify a front camera, try cycling until we get to a front-facing one
        logger.debug('No front camera ID found, trying to cycle cameras');
        let attempts = 0;
        const maxAttempts = 3; // Prevent infinite loops

        const cycleUntilFront = async () => {
          if (attempts >= maxAttempts) return;

          try {
            const camera = await frameRef.current.cycleCamera();
            attempts++;

            logger.debug('Cycled to camera:', camera);

            // Check if we've found a front-facing camera
            if (camera && (camera.facing === 'user' ||
              (camera.label && (
                camera.label.toLowerCase().includes('front') ||
                camera.label.toLowerCase().includes('user') ||
                camera.label.toLowerCase().includes('selfie')
              )))) {
              logger.debug('Successfully switched to front camera');
            } else if (attempts < maxAttempts) {
              // Try again if we haven't reached the maximum attempts
              await cycleUntilFront();
            }
          } catch (error) {
            logger.error('Error cycling camera:', error);
          }
        };

        await cycleUntilFront();
      }
    } catch (error) {
      logger.error('Error ensuring front camera:', error);
    }
  }, [callState]);

  // Add Sentry logging for device changes
  const handleDeviceChange = useCallback(async () => {
    if (!frameRef.current || frameRef.current._destroyed || callState !== 'joined') {
      logger.debug('Cannot update audio device: not in a call');
      return;
    }

    try {
      logger.debug('Device change detected, updating audio settings...');
      logDeviceEvent('change detected');

      // Get list of available audio devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioDevices = devices.filter(device => device.kind === 'audioinput');

      logger.debug('Available audio devices:', audioDevices);

      // If we have audio devices, update the input settings
      if (audioDevices.length > 0) {
        const preferredAudioDevice = audioDevices[audioDevices.length - 1];

        logDeviceEvent('setting preferred audio device', {
          deviceLabel: preferredAudioDevice.label,
          deviceId: preferredAudioDevice.deviceId
        });

        // Update audio settings with the new device
        await frameRef.current.updateInputSettings({
          audio: {
            settings: {
              deviceId: preferredAudioDevice.deviceId,
              // Maintain other audio settings that help with call quality
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            },
            processor: {
              type: 'noise-cancellation',
            },
          }
        });

        logger.debug('Audio device updated successfully');
      }
    } catch (error) {
      logDeviceError(error, {
        tags: { action: 'device_change' },
        extra: { callState }
      });
      logger.error('Error updating audio device:', error);
    }
  }, [frameRef, callState]);

  // Initialize Daily iframe
  useEffect(() => {
    logger.debug('Setting up Daily iframe hook');

    // Create container first
    const container = ensureContainer();
    logger.debug('Using container:', container);

    // If we don't have a frame yet, create one
    if (!dailyFrameRef.current || dailyFrameRef.current._destroyed) {
      logger.debug('Creating new Daily iframe instance');

      try {
        // Create a hidden iframe
        dailyFrameRef.current = DailyIframe.createFrame(container, {
          showLeaveButton: false, // Hide default UI elements
          showFullscreenButton: false,
          // facingMode: 'user', // Initial preference, will be enforced with setCamera later
          showLocalVideo: true, // Show local video for debugging
          showParticipantsBar: true, // Show participants for debugging
          iframeStyle: {
            width: '100%',
            height: '100%',
            border: '0',
          }
        });

        // Store in component ref
        frameRef.current = dailyFrameRef.current;

        logger.debug('Daily iframe created successfully:', dailyFrameRef.current);
      } catch (error) {
        logger.error('Error creating Daily iframe:', error);
        setCallState('error');
        return;
      }
    } else {
      // Use existing frame
      logger.debug('Using existing Daily iframe instance');
      frameRef.current = dailyFrameRef.current;
    }

    // Define event handlers
    const handleJoinedMeeting = (event) => {
      logger.debug(' Joined meeting event received:', event);
      setCallState('joined');

      // Update participants
      try {
        const participantData = frameRef.current.participants();
        logger.debug('Participants data:', participantData);
        setParticipants(participantData);

        // Store wsMessageEvent in localStorage before proctoring steps
        if (wsMessageEvent) {
          localStorage.setItem('wsMessageEvent', JSON.stringify(wsMessageEvent));
        }

        // Ensure front camera is used after joining
        ensureFrontCamera();

        // Set up device change listener to handle Bluetooth headsets
        navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

        // Initial audio device setup
        handleDeviceChange();

        // Only start screen sharing if this is the host/initial participant
        const isHost = Object.keys(participantData).length >= 1;
        if (isHost) {
          logger.debug('Host detected, starting proctoring setup...');
          
          // Complete proctoring steps
          const completeProctoring = async () => {
            try {
              // Wait for camera to be placed
              await new Promise((resolve) => {
                const checkCameraPlaced = () => {
                  if (localStorage.getItem('camera_placed') === 'true') {
                    resolve();
                  } else {
                    setTimeout(checkCameraPlaced, 1000);
                  }
                };
                checkCameraPlaced();
              });

              // After camera is placed, start screen recording if enabled
              if (wsMessageEvent?.screen_record) {
                logger.debug('Starting screen share after proctoring...');
                setTimeout(() => {
                  startScreenShare().then(() => {
                    // Send screen share permitted event
                    const { isScreenShared } = useIsScreenShared();
                    logger.debug('isScreenShared>>>>>>>>>', isScreenShared);

                    if (isScreenShared) {
                      screenRecordPermitted();
                    } else {
                      logger.debug('Screen share not detected', isScreenShared);
                    }
                  });
                }, 3000);
              } else {
                logger.debug('Screen recording is disabled in wsMessageEvent');
              }
            } catch (error) {
              logger.error('Error in proctoring setup:', error);
            }
          };

          completeProctoring();
        }

        // Report joined event to WebSocket
        sendVideoCallEvent('joined_meeting', {
          participants: Object.keys(participantData).length
        });
      } catch (error) {
        logger.error('Error getting participants:', error);
      }

      if (callbacksRef.current.onJoined) {
        callbacksRef.current.onJoined(frameRef.current.participants());
      }
    };

    const handleLeftMeeting = (event) => {
      logger.debug('Left meeting event received:', event);
      setCallState('left');
      setParticipants({});
      isJoining = false;
      activeRoomUrl = null;

      // Stop screen sharing if active
      if (isScreenSharing) {
        stopScreenShare();
      }

      // Remove device change listener when leaving the call
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);

      // Report left event to WebSocket
      sendVideoCallEvent('left_meeting');

      if (callbacksRef.current.onLeft) {
        callbacksRef.current.onLeft();
      }
    };

    const handleParticipantJoined = (event) => {
      logger.debug('Participant joined event:', event);

      // Update all participants to ensure consistent state
      try {
        setParticipants(frameRef.current.participants());
        //  startScreenShare();

        // Report participant joined event to WebSocket
        sendVideoCallEvent('participant_joined', {
          participant_id: event.participant.session_id,
          participant_name: event.participant.user_name || 'Unknown',
          is_local: event.participant.local
        });
      } catch (error) {
        logger.error('Error getting participants on join:', error);
      }

      if (callbacksRef.current.onParticipantJoined) {
        callbacksRef.current.onParticipantJoined(event.participant);
      }
    };

    const handleParticipantLeft = (event) => {
      logger.debug('Participant left event:', event);

      // Update all participants to ensure consistent state
      try {
        setParticipants(frameRef.current.participants());

        // Report participant left event to WebSocket
        sendVideoCallEvent('participant_left', {
          participant_id: event.participant.session_id,
          participant_name: event.participant.user_name || 'Unknown',
          is_local: event.participant.local
        });
      } catch (error) {
        logger.error('Error getting participants on leave:', error);
      }

      if (callbacksRef.current.onParticipantLeft) {
        callbacksRef.current.onParticipantLeft(event.participant);
      }
    };

    const handleParticipantUpdated = (event) => {
      // Update all participants to ensure consistent state
      try {
        setParticipants(frameRef.current.participants());
      } catch (error) {
        logger.error('Error getting participants on update:', error);
      }

      if (callbacksRef.current.onParticipantUpdated) {
        callbacksRef.current.onParticipantUpdated(event.participant);
      }
    };

    const handleError = (error) => {
      logger.error('Daily error event:', error);
      setCallState('error');
      isJoining = false;

      // Report error event to WebSocket
      sendVideoCallEvent('error', {
        error: error.message || 'Unknown error'
      });

      if (callbacksRef.current.onError) {
        callbacksRef.current.onError(error);
      }
    };

    const handleJoiningMeeting = (event) => {
      logger.debug('Joining meeting event received:', event);
    };

    // Log incoming tracks for debugging
    const handleTrackStarted = (event) => {
      logger.debug('Track started:', event.participant.session_id, event.track.kind);

      // Report track started event to WebSocket
      sendVideoCallEvent('track_started', {
        participant_id: event.participant.session_id,
        track_kind: event.track.kind
      });
    };

    // Log track issues for debugging
    const handleTrackStopped = (event) => {
      logger.debug('Track stopped:', event.participant.session_id, event.track.kind);

      // Report track stopped event to WebSocket
      sendVideoCallEvent('track_stopped', {
        participant_id: event.participant.session_id,
        track_kind: event.track.kind
      });
    };

    const handleEvents = (event) => {
      // Report event to WebSocket if connected
      sendVideoCallEvent(event.action || event.type, {
        details: event
      });
    };

    //

    // participant-joined
    // participant-updated
    // participant-left
    // waiting-participant-added
    // waiting-participant-updated
    // waiting-participant-removed
    // app-message
    // track-started
    // track-stopped
    // lang-updated
    // Quality events
    // network-connection
    // network-quality-change
    // cpu-load-change

    // Register event handlers
    logger.debug('Registering event handlers on Daily iframe');
    frameRef.current.on('joining-meeting', handleJoiningMeeting);
    frameRef.current.on('joined-meeting', handleJoinedMeeting);
    frameRef.current.on('left-meeting', handleLeftMeeting);
    frameRef.current.on('participant-joined', handleParticipantJoined);
    frameRef.current.on('participant-left', handleParticipantLeft);
    frameRef.current.on('participant-updated', handleParticipantUpdated);
    frameRef.current.on('error', handleError);
    frameRef.current.on('track-started', handleTrackStarted);
    frameRef.current.on('track-stopped', handleTrackStopped);
    frameRef.current.on('loading', handleEvents);
    frameRef.current.on('loaded', handleEvents);
    frameRef.current.on('load-attempt-failed', handleEvents);
    frameRef.current.on('started-camera', handleEvents);
    frameRef.current.on('camera-error', handleEvents);
    frameRef.current.on('call-instance-destroyed', handleEvents);
    frameRef.current.on('meeting-session-updated', handleEvents);
    frameRef.current.on('test-completed', handleEvents);
    frameRef.current.on('fullscreen', handleEvents);
    frameRef.current.on('exited-fullscreen', handleEvents);
    frameRef.current.on('active-speaker-change', handleEvents);
    frameRef.current.on('active-speaker-mode-change', handleEvents);
    frameRef.current.on('access-state-updated', handleEvents);
    frameRef.current.on('theme-updated', handleEvents);
    frameRef.current.on('receive-settings-updated', handleEvents);
    frameRef.current.on('send-settings-updated', handleEvents);
    frameRef.current.on('show-local-video-changed', handleEvents);
    frameRef.current.on('input-settings-updated', handleEvents);
    frameRef.current.on('nonfatal-error', handleEvents);
    frameRef.current.on('custom-button-click', handleEvents);
    frameRef.current.on('local-screen-share-started', (event) => {
      handleEvents(event);
      setIsScreenSharing(true);
    });
    frameRef.current.on('local-screen-share-stopped', (event) => {
      handleEvents(event);
      setIsScreenSharing(false);
    });
    frameRef.current.on('local-screen-share-canceled', (event) => {
      handleEvents(event);
      setIsScreenSharing(false);
    });
    frameRef.current.on('local-audio-level', handleEvents);
    frameRef.current.on('remote-participants-audio-level', handleEvents);
    frameRef.current.on('face-counts-updated', handleEvents);
    frameRef.current.on('participant-joined', handleEvents);
    frameRef.current.on('participant-updated', handleEvents);
    frameRef.current.on('participant-left', handleEvents);
    frameRef.current.on('waiting-participant-added', handleEvents);
    frameRef.current.on('waiting-participant-updated', handleEvents);
    frameRef.current.on('waiting-participant-removed', handleEvents);
    frameRef.current.on('app-message', handleEvents);
    frameRef.current.on('track-started', handleEvents);
    frameRef.current.on('track-stopped', handleEvents);
    frameRef.current.on('lang-updated', handleEvents);
    frameRef.current.on('network-connection', handleEvents);
    frameRef.current.on('network-quality-change', handleEvents);
    frameRef.current.on('cpu-load-change', handleEvents);








    // Cleanup on unmount
    return () => {
      logger.debug('Cleaning up Daily iframe event handlers');

      // Remove device change listener
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);

      // Unregister event handlers for this component
      if (frameRef.current) {
        logger.debug('Removing event listeners');

        frameRef.current.off('joining-meeting', handleJoiningMeeting);
        frameRef.current.off('joined-meeting', handleJoinedMeeting);
        frameRef.current.off('left-meeting', handleLeftMeeting);
        frameRef.current.off('participant-joined', handleParticipantJoined);
        frameRef.current.off('participant-left', handleParticipantLeft);
        frameRef.current.off('participant-updated', handleParticipantUpdated);
        frameRef.current.off('error', handleError);
        frameRef.current.off('track-started', handleTrackStarted);
        frameRef.current.off('track-stopped', handleTrackStopped);




      }
    };
  }, [ensureContainer]);

  // Add Sentry logging for call state changes
  useEffect(() => {
    logger.debug('Call state changed:', callState);
    logCallStateChange(callState, {
      activeRoomUrl
    });
  }, [callState, logCallStateChange]);

  // Add Sentry logging for participant changes
  useEffect(() => {
    logger.debug('Participants updated:', Object.keys(participants).length);
    logParticipantEvent('updated', {
      participantCount: Object.keys(participants).length,
      callState
    });
  }, [participants, logParticipantEvent, callState]);

  // Join meeting function
  const joinCall = useCallback((roomUrl, audioEnabled = true, videoEnabled = true) => {
    if (!roomUrl) {
      logMessage('No room URL provided for join call', 'error');
      logger.error('No room URL provided');
      return;
    }

    // Validate iframe instance
    if (!frameRef.current || frameRef.current._destroyed) {
      logMessage('Daily iframe instance not available or destroyed', 'error', {
        tags: { action: 'join_call' }
      });
      logger.error('Daily iframe instance not available or destroyed');
      setCallState('error');
      return;
    }

    // Don't rejoin the same room or if already joining
    if (activeRoomUrl === roomUrl || isJoining || callState === 'joined' || callState === 'joining') {
      logger.debug('Already in this room or joining process in progress');
      return;
    }

    logVideoCallEvent('Joining call', {
      roomUrl,
      audioEnabled,
      videoEnabled
    });

    // Process URL
    const { baseUrl, params } = getUrlAndParams(roomUrl);
    const token = params?.t;

    logger.debug(`Joining room: ${baseUrl}`);
    setCallState('joining');
    isJoining = true;
    activeRoomUrl = roomUrl;

    // Set media state
    setIsMuted(!audioEnabled);
    setIsVideoOff(!videoEnabled);

    // Join options
    const joinOptions = {
      url: roomUrl
    };

    // Add token if available
    if (token) {
      joinOptions.token = token;
    }

    // Add media settings with front camera configuration
    joinOptions.audioSource = audioEnabled;

    // Configure video with front camera preference
    if (videoEnabled) {
      // Daily.co expects videoSource to be true, false, or a MediaStreamTrack
      // Using true lets Daily.co handle camera selection
      joinOptions.videoSource = true;

      // Set camera properties through the dailyConfig
      joinOptions.dailyConfig = {
        // Always use front camera when available
        preferredCamera: 'user',
        // Set video constraints
        videoConstraints: {
          width: isMobileDevice() ? { ideal: 640, max: 1280 } : { ideal: 1280 },
          height: isMobileDevice() ? { ideal: 480, max: 720 } : { ideal: 720 }
        }
      };
      logger.debug('Using front camera configuration with dailyConfig:', joinOptions.dailyConfig);
    } else {
      joinOptions.videoSource = false;
    }

    // For debugging
    logger.debug('Join options:', joinOptions);

    // Join the call
    try {
      frameRef.current.join(joinOptions).catch(err => {
        logVideoCallError(err, {
          tags: { action: 'join_call' },
          extra: { callState }
        });
        logger.error('Error joining meeting:', err);
        setCallState('error');
        isJoining = false;
        activeRoomUrl = null;

        if (callbacksRef.current.onError) {
          callbacksRef.current.onError(err);
        }
      });
    } catch (error) {
      logVideoCallError(error, {
        tags: { action: 'join_call' },
        extra: { callState }
      });
      logger.error('Exception when joining meeting:', error);
      setCallState('error');
      isJoining = false;
      activeRoomUrl = null;
    }
  }, [callState, frameRef]);

  // Leave call function
  const leaveCall = useCallback(() => {
    logger.debug('Leave call requested, current call state:', callState);
    logVideoCallEvent('Leave call requested', {
      callState,
      activeRoomUrl
    });

    if (!frameRef.current || frameRef.current._destroyed) {
      logMessage('Daily iframe instance not available or destroyed during leave call', 'error', {
        tags: { action: 'leave_call' }
      });
      logger.error('Daily iframe instance not available or destroyed');
      setCallState('left');
      isJoining = false;
      activeRoomUrl = null;
      return;
    }

    if (callState === 'joined' || callState === 'joining') {
      logger.debug('Leaving call');
      setCallState('leaving');

      frameRef?.current?.leave().catch(err => {
        logVideoCallError(err, {
          tags: { action: 'leave_call' },
          extra: { callState }
        });
        logger.error('Error leaving meeting:', err);
        setCallState('error');

        if (callbacksRef?.current?.onError) {
          callbacksRef?.current.onError(err);
        }
      });
    } else {
      logger.debug('Not in a call to leave');
    }
  }, [callState]);

  // Toggle local audio
  const toggleMute = useCallback(() => {
    if (!frameRef.current || frameRef.current._destroyed || callState !== 'joined') {
      return;
    }

    const newMuteState = !isMuted;
    logger.debug(`Setting audio to: ${!newMuteState}`);
    frameRef.current.setLocalAudio(!newMuteState);
    setIsMuted(newMuteState);
  }, [isMuted, callState]);


  const muteAll = useCallback(() => {
    if (!frameRef.current || frameRef.current._destroyed || callState !== 'joined') {
      return;
    }

    frameRef.current.setLocalAudio(true);
    setIsMuted(true);
  }, [callState]);


  const soundLocalOff = useCallback(() => {
    if (!frameRef.current || frameRef.current._destroyed || callState !== 'joined') {
      return;
    }

    frameRef.current.setLocalAudio(true);
    setIsMuted(true);
  }, [callState]);



  // Toggle local video
  const toggleVideo = useCallback(() => {
    if (!frameRef.current || frameRef.current._destroyed || callState !== 'joined') {
      return;
    }

    const newVideoState = !isVideoOff;
    logger.debug(`Setting video to: ${!newVideoState}`);

    // If turning video back on, ensure front camera is used
    if (!newVideoState) {
      // First turn video on with basic constraints
      frameRef.current.setLocalVideo({
        // facingMode: 'user', // Always use front camera
        width: isMobileDevice() ? { ideal: 640, max: 1280 } : { ideal: 1280 },
        height: isMobileDevice() ? { ideal: 480, max: 720 } : { ideal: 720 }
      });

      // Then ensure front camera is explicitly selected
      setTimeout(() => {
        ensureFrontCamera();
      }, 500); // Small delay to ensure video is initialized first
    } else {
      frameRef.current.setLocalVideo(false);
    }

    setIsVideoOff(newVideoState);
  }, [isVideoOff, callState, ensureFrontCamera]);

  // Get video and audio streams for a participant to use in custom UI
  const getParticipantTracks = useCallback((participantId) => {
    if (!frameRef.current || !participants[participantId]) {
      return { audioTrack: null, videoTrack: null };
    }

    const participant = participants[participantId];
    return {
      audioTrack: participant?.audioTrack?.track || null,
      videoTrack: participant?.videoTrack?.track || null
    };
  }, [participants]);

  // Screen sharing methods
  const startScreenShare = useCallback(async () => {
    logger.debug('Attempting to start screen sharing...');
    logScreenShareEvent('attempting to start');

    if (frameRef.current) {
      try {
        await frameRef.current.startScreenShare();
        logger.debug('Screen sharing started successfully');
        setIsScreenSharing(true);
        // Set camera_placed to false in localStorage when screen sharing starts
        localStorage.setItem('camera_placed', 'false');

        logScreenShareEvent('started successfully');

        // Send screen share event to WebSocket
        if (ws && ws.readyState === WebSocket.OPEN) {
          logger.debug('Sending screen record permitted event...');
          const message = {
            type:"client_event",
            message:"screen_record_permitted",
            timestamp:new Date().toISOString()
          };
          logger.debug('Screen share message:', message);
          ws.send(JSON.stringify(message));
        } else {
          logger.warn('WebSocket not connected for sending screen share event. State:', ws?.readyState);
        }

        // Report screen share start event to WebSocket
        screenRecordPermitted();

        return true;
      } catch (error) {
        logScreenShareError(error, {
          tags: { action: 'start_screen_share' },
          extra: { callState }
        });
        logger.error('Error starting screen share:', error);

        // Report screen share error to WebSocket
        sendVideoCallEvent('screen_share_error', {
          error: error.message || 'Unknown error'
        });

        return false;
      }
    } else {
      logMessage('Cannot start screen sharing: call not joined or frame not available', 'warning', {
        tags: { action: 'start_screen_share' }
      });
      logger.warn('Cannot start screen sharing: call not joined or frame not available');
      return false;
    }
  }, [callState, ws]);


  const stopScreenShare = useCallback(() => {
    logger.debug('Stopping screen sharing...');
    logScreenShareEvent('stopping');

    if (frameRef.current && isScreenSharing) {
      try {
        frameRef.current.stopScreenShare();
        logger.debug('Screen sharing stopped successfully');
        setIsScreenSharing(false);

        logScreenShareEvent('stopped successfully');

        // Report screen share stop event to WebSocket
        sendVideoCallEvent('screen_share_stopped');

        return true;
      } catch (error) {
        
        logScreenShareError(error, {
          tags: { action: 'stop_screen_share' },
          extra: { callState }
        });
        logger.error('Error stopping screen share:', error);
        return false;
      }
    } else {
      logMessage('Cannot stop screen sharing: not currently sharing or frame not available', 'warning', {
        tags: { action: 'stop_screen_share' }
      });
      logger.warn('Cannot stop screen sharing: not currently sharing or frame not available');
      return false;
    }
  }, [isScreenSharing, ws]);

  // Return the hook API
  return {
    callFrame: frameRef.current,
    participants,
    callState,
    isMuted,
    isVideoOff,
    isScreenSharing,
    joinCall,
    leaveCall,
    toggleMute,
    toggleVideo,
    startScreenShare,
    stopScreenShare,
    getParticipantTracks,
    isInCall: callState === 'joined',
    currentRoom: activeRoomUrl,
    soundLocalOff,
    muteAll,
  };
};

export default useDaily;